import type { InvoiceData } from "@/apis/gql/types";
import { arrayMove } from "@dnd-kit/sortable";
import { PDFDocument } from "pdf-lib";
import { useEffect, useState } from "react";
import type { EditablePage } from "../types";

interface UsePageManagementProps {
  pages: EditablePage[];
  onPagesChange: (pages: EditablePage[]) => void;
}

export const usePageManagement = ({
  pages,
  onPagesChange,
}: UsePageManagementProps) => {
  const [splits, setSplits] = useState<number[]>([]);
  const [splitEvery, setSplitEvery] = useState(false);
  const [splitEveryValue, setSplitEveryValue] = useState(1);

  useEffect(() => {
    if (splitEvery && splitEveryValue <= pages.length) {
      setSplits(
        Array.from(
          new Array(Math.floor(pages.length / splitEveryValue) - 1),
          (el, index) => (index + 1) * splitEveryValue - 1,
        ),
      );
    } else {
      setSplits([]);
    }
  }, [splitEvery, splitEveryValue, pages.length]);

  const handleSplit = (pageIndex: number) => {
    if (splits.includes(pageIndex)) {
      setSplits(splits.filter((split) => split !== pageIndex));
    } else {
      setSplits([...splits, pageIndex]);
    }
  };

  const handleDeletePage = (pageIndex: number) => {
    const newPages = pages.map((p, index) => {
      if (index === pageIndex) {
        return { ...p, markedForDeletion: !p.markedForDeletion };
      }
      return p;
    });

    onPagesChange(newPages);
  };

  const handleAddInvoice = async (
    invoice: InvoiceData,
    supplierName: string,
    pdfUrl: string,
  ) => {
    const response = await fetch(pdfUrl);
    const pdf = await PDFDocument.load(await response.arrayBuffer());
    const pageCount = pdf.getPageCount();

    const newPages: EditablePage[] = [];
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      newPages.push({
        id: `${invoice.invoiceId}-${pageIndex}-${Date.now()}`,
        sourcePdfUrl: pdfUrl,
        sourcePageIndex: pageIndex,
        sourceInvoiceId: invoice.invoiceId,
        sourceInvoiceFileName: invoice.fileName,
        currentDisplayIndex: pages.length + pageIndex,
      });
    }

    const updatedPages = [...pages, ...newPages];
    onPagesChange(updatedPages);
  };

  const handleMove = (oldIndex: number, newIndex: number) => {
    const newPages = arrayMove(pages, oldIndex, newIndex);
    onPagesChange(newPages);

    // Update splits
    const newSplits = splits.map((split) => {
      if (split === oldIndex) return newIndex;
      if (oldIndex < newIndex) {
        if (split > oldIndex && split <= newIndex) return split - 1;
      } else {
        if (split >= newIndex && split < oldIndex) return split + 1;
      }
      return split;
    });

    setSplits(newSplits);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseInt(event.target.value, 10);
    if (value && value > 0) {
      setSplitEveryValue(value);
    }
  };

  return {
    // State
    splits,
    splitEvery,
    splitEveryValue,

    // Actions
    handleSplit,
    handleDeletePage,
    handleAddInvoice,
    handleMove,
    handleInputChange,
    setSplitEvery,

    // Computed values
    splitCount: splits.length === 0 ? 0 : splits.length + 1,
  };
};
